// Comparação entre duas runs da MESMA base. Trabalha em cima do objeto
// `metrics` que já vem em cada TestRun (testSetsApi.detail) — não há fetch novo.
// A = run de referência (antes), B = run comparada (depois).

import type { TestRun, TestRunMetrics } from '@sophia/shared-schemas'

// ---- métricas derivadas (não recalculam a fórmula, só agregam contadores) ----

// Falsos positivos globais = linhas classificadas numa classe errada.
export function falsePositives(m: TestRunMetrics): number {
  return m.classified - m.classifiedCorrect
}

// Falsos negativos = linhas de classes cadastradas que não foram corretamente
// identificadas (foram pra classe errada ou não classificadas).
export function falseNegatives(m: TestRunMetrics): number {
  return m.perClass
    .filter((c) => c.classId !== null)
    .reduce((acc, c) => acc + (c.total - c.correctlyPredicted), 0)
}

export type DeltaFormat = 'pct' | 'count'

export interface MetricDelta {
  key: string
  label: string
  aValue: number
  bValue: number
  delta: number // pct: diferença em pontos percentuais já × maybe; count: absoluto
  format: DeltaFormat
  higherIsBetter: boolean
  // true = melhorou, false = piorou, null = sem mudança
  improved: boolean | null
}

function buildDelta(
  key: string,
  label: string,
  aValue: number,
  bValue: number,
  format: DeltaFormat,
  higherIsBetter: boolean,
): MetricDelta {
  const delta = bValue - aValue
  let improved: boolean | null = null
  if (delta !== 0) improved = higherIsBetter ? delta > 0 : delta < 0
  return { key, label, aValue, bValue, delta, format, higherIsBetter, improved }
}

export interface ProblematicRule {
  ruleId: string
  ruleName: string
  className: string
  precisionA: number | null // null = regra não existia/não rodou na run A
  precisionB: number
  fpA: number | null
  fpB: number
}

export interface RunComparison {
  rows: MetricDelta[]
  classesImpacted: number
  problematicRules: ProblematicRule[]
  summary: string
}

export function compareRuns(runA: TestRun, runB: TestRun): RunComparison | null {
  const a = runA.metrics
  const b = runB.metrics
  if (!a || !b) return null

  const rows: MetricDelta[] = [
    buildDelta('precision', 'Precisão', a.precisionGlobal, b.precisionGlobal, 'pct', true),
    buildDelta('coverage', 'Cobertura', a.coverageGlobal, b.coverageGlobal, 'pct', true),
    buildDelta('classified', 'Total classificado', a.classified, b.classified, 'count', true),
    buildDelta('notClassified', 'Não classificado', a.notClassified, b.notClassified, 'count', false),
    buildDelta('fp', 'Falsos positivos', falsePositives(a), falsePositives(b), 'count', false),
    buildDelta('fn', 'Falsos negativos', falseNegatives(a), falseNegatives(b), 'count', false),
    buildDelta('conflicts', 'Conflitos de classe', a.conflictRows, b.conflictRows, 'count', false),
  ]

  // Classes impactadas: classes (por id/nome) cujas contagens mudaram entre runs.
  const classKey = (c: { classId: string | null; className: string }) => c.classId ?? `__${c.className}`
  const aClassMap = new Map(a.perClass.map((c) => [classKey(c), c]))
  const allClassKeys = new Set<string>([...a.perClass.map(classKey), ...b.perClass.map(classKey)])
  let classesImpacted = 0
  for (const key of allClassKeys) {
    const ac = aClassMap.get(key)
    const bc = b.perClass.find((c) => classKey(c) === key)
    if (!ac || !bc) {
      classesImpacted += 1
      continue
    }
    if (
      ac.correctlyPredicted !== bc.correctlyPredicted ||
      ac.misclassified !== bc.misclassified ||
      ac.notClassified !== bc.notClassified ||
      ac.predictedCount !== bc.predictedCount
    ) {
      classesImpacted += 1
    }
  }

  // Regras problemáticas: regras que pioraram em B (precisão caiu OU FP aumentou).
  const aRuleMap = new Map(a.perRule.map((r) => [r.ruleId, r]))
  const problematicRules: ProblematicRule[] = []
  for (const rb of b.perRule) {
    const ra = aRuleMap.get(rb.ruleId) ?? null
    const precisionDropped = ra ? rb.precision < ra.precision : false
    const fpIncreased = ra ? rb.falsePositives > ra.falsePositives : rb.falsePositives > 0
    if (precisionDropped || fpIncreased) {
      problematicRules.push({
        ruleId: rb.ruleId,
        ruleName: rb.ruleName,
        className: rb.className,
        precisionA: ra ? ra.precision : null,
        precisionB: rb.precision,
        fpA: ra ? ra.falsePositives : null,
        fpB: rb.falsePositives,
      })
    }
  }
  problematicRules.sort((x, y) => y.fpB - x.fpB)

  return {
    rows,
    classesImpacted,
    problematicRules,
    summary: buildSummary(rows),
  }
}

// Resumo textual executivo a partir dos deltas mais relevantes.
const GAIN_PHRASE: Record<string, string> = {
  precision: 'aumentou a precisão',
  coverage: 'melhorou a cobertura',
  classified: 'classificou mais linhas',
  notClassified: 'reduziu as linhas não classificadas',
  fp: 'reduziu os falsos positivos',
  fn: 'reduziu os falsos negativos',
  conflicts: 'reduziu os conflitos de classe',
}
const LOSS_PHRASE: Record<string, string> = {
  precision: 'reduziu a precisão',
  coverage: 'reduziu a cobertura',
  classified: 'classificou menos linhas',
  notClassified: 'aumentou as linhas não classificadas',
  fp: 'aumentou os falsos positivos',
  fn: 'aumentou os falsos negativos',
  conflicts: 'aumentou os conflitos de classe',
}

function buildSummary(rows: MetricDelta[]): string {
  const gains: string[] = []
  const losses: string[] = []
  for (const r of rows) {
    if (r.improved === true) gains.push(GAIN_PHRASE[r.key] ?? r.label)
    else if (r.improved === false) losses.push(LOSS_PHRASE[r.key] ?? r.label)
  }
  if (gains.length === 0 && losses.length === 0) {
    return 'As duas runs têm métricas idênticas — nenhuma mudança entre elas.'
  }
  const join = (items: string[]) =>
    items.length <= 1 ? items[0] : `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`

  if (gains.length > 0 && losses.length > 0) {
    return `A nova run ${join(gains)}, mas ${join(losses)}.`
  }
  if (gains.length > 0) return `A nova run ${join(gains)}.`
  return `A nova run ${join(losses)}.`
}
